import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { captureTrackingParams, buildUrlWithTrackingParams } from '@/lib/trackingParams';

/**
 * Ensures tracking parameters (gclid, utm_*) persist in the address bar
 * across all client-side navigations within a single browser session.
 *
 * On mount: captures tracking params from the initial URL into sessionStorage.
 * On every route change: re-appends stored tracking params to the current
 * URL via history.replaceState (no new history entry — back/forward work
 * normally).
 *
 * This component must be rendered inside <BrowserRouter>.
 *
 * Technical notes:
 * - We use window.history.replaceState (not React Router's navigate) to avoid
 *   triggering a re-render loop. replaceState updates the address bar without
 *   changing React Router's internal location state.
 * - We preserve window.history.state (React Router's internal { usr, key, idx }
 *   object) to avoid breaking its navigation state.
 * - GA4's url_passthrough: true (set in index.html) automatically reads the
 *   query string from the address bar on each page view, so keeping tracking
 *   params in the URL is sufficient for attribution.
 */
export function TrackingParamsPersister() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    // Capture any tracking params from the current URL. This handles:
    // - Initial landing from a Google Ads link (gclid in URL)
    // - Any external link arriving with utm_ params on any page
    // - Re-capture on back/forward (idempotent — same values, no change)
    captureTrackingParams();

    // Re-append stored tracking params to the address bar if they're missing.
    const newUrl = buildUrlWithTrackingParams(pathname, search);
    if (newUrl) {
      // Preserve React Router's history state to avoid breaking navigation.
      window.history.replaceState(window.history.state, '', newUrl);
    }
  }, [pathname, search]);

  return null;
}
